import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import Calendar from '@components/calendar/Calendar'
import Icon from '@components/icon/Icons'
import Modal from '@components/modal/Modal'
import Drawer from '@components/drawer/Drawer'
import Button from '@components/button/Button'

import useI18n from '@i18n/useI18n'

import { isBefore, startOfDay, endOfDay } from 'date-fns'

interface Props {
  start?: Date
  end?: Date
  onDates: (start?: Date, end?: Date) => void
  required?: boolean
  onClose?: () => void
  min?: Date
  max?: Date
}

const CalendarDatePickerComponent = ({ start, end, onDates, onClose, required = true, min, max }: Props) => {
  const i18n = useI18n()

  const [from, setFrom] = React.useState(start)
  const [to, setTo] = React.useState(end)
  const [selected, setSelected] = React.useState<'from' | 'to'>(start ? 'to' : 'from')

  const erase = () => {
    setFrom(undefined)
    setTo(undefined)
    setSelected('from')
  }

  const submit = () => {
    onDates(from, to)

    if (onClose) {
      onClose()
    } else {
      CalendarDatePicker.close()
    }
  }

  const onDateSelected = (date: Date) => {
    if (selected === 'from') {
      setFrom(startOfDay(date))
      setTo(undefined)
      setSelected('to')
    } else {
      setTo(endOfDay(date))
      setSelected('from')

      if (from && isBefore(date, from)) {
        setFrom(date)
      }
    }
  }

  return (
    <MainContainer>
      <Header>
        <TouchableOpacity activeOpacity={0.8} onPress={onClose || CalendarDatePicker.close}>
          <Icon name="arrow-left" size={30} />
        </TouchableOpacity>
        <Title>{i18n.t('components.datePicker.title')}</Title>
        <TouchableOpacity activeOpacity={0.8} onPress={erase}>
          <Erase disabled={!from && !to}>{i18n.t('common.erase')}</Erase>
        </TouchableOpacity>
      </Header>
      <Dates>
        <DateContainer activeOpacity={0.8} onPress={() => setSelected('from')}>
          <Prefix>{`${i18n.t('components.datePicker.from')} `}</Prefix>

          <DateDisplayContainer selected={selected === 'from'}>
            <DateDisplay>
              {from ? i18n.t('components.datePicker.date', { date: from }) : i18n.t('components.datePicker.empty')}
            </DateDisplay>
          </DateDisplayContainer>
        </DateContainer>
        <DateContainer activeOpacity={0.8} onPress={() => setSelected('to')}>
          <Prefix>{`${i18n.t('components.datePicker.to')} `}</Prefix>
          <DateDisplayContainer selected={selected === 'to'}>
            <DateDisplay>
              {to ? i18n.t('components.datePicker.date', { date: to }) : i18n.t('components.datePicker.empty')}
            </DateDisplay>
          </DateDisplayContainer>
        </DateContainer>
      </Dates>

      <Content>
        <CalendarContainer>
          <Calendar
            onDateSelected={onDateSelected}
            date={from}
            range={from && to ? { start: from, end: to } : undefined}
            min={min}
            max={max}
          />
        </CalendarContainer>
      </Content>

      <ButtonContainer>
        <Button
          label={i18n.t('common.validate')}
          onClick={submit}
          status={(from && to) || !required ? 'active' : 'disabled'}
        />
      </ButtonContainer>
    </MainContainer>
  )
}

const MainContainer = styled(View)`
  ${(props) => props.theme.breakpoint !== 'phone' && `background-color: ${props.theme.colors.background};`}
  ${(props) => props.theme.breakpoint !== 'phone' && `border-radius: 8px;`}
  ${(props) => props.theme.breakpoint !== 'phone' && `padding: 16px;`}
  ${(props) => props.theme.breakpoint === 'phone' && `flex: 1;`}
`

const Header = styled(View)`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 0 15px;
  margin-bottom: 15px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
`

const Erase = styled(Text)<{ disabled?: boolean }>`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => (props.disabled ? props.theme.colors.disable : props.theme.colors.primary)};
`

const Prefix = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`

const Dates = styled(View)`
  flex-direction: row;
  align-items: center;
  margin: 15px;
`

const DateContainer = styled(TouchableOpacity)`
  width: 150px;
  flex-direction: row;
`

const DateDisplayContainer = styled(View)<{ selected: boolean }>`
  border: 0 solid ${(props) => (props.selected ? props.theme.colors.primary : 'transparent')};
  border-bottom-width: 2px;
`

const DateDisplay = styled(Prefix)`
  color: ${(props) => props.theme.colors.secondaryText};
`

const Content = styled(View)`
  ${(props) => props.theme.breakpoint === 'phone' && 'flex: 1'};
`

const CalendarContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  border-radius: 8px;
  padding: 15px 0;
  ${(props) => props.theme.breakpoint === 'phone' && 'margin-top: 15px'};
`

const ButtonContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  padding: 16px;
`

interface PickerProps extends Props {
  mode: 'drawer' | 'popup'
}

const CalendarDatePicker = {
  open: ({ mode, ...config }: PickerProps) => {
    if (mode === 'popup') {
      Modal.open({ Content: () => <CalendarDatePickerComponent {...config} />, cancel: true })
    } else {
      Drawer.open({ render: () => <CalendarDatePickerComponent {...config} />, hideTarget: true })
    }
  },
  close: () => {
    Modal.close()
    Drawer.close()
  },
}

export { CalendarDatePickerComponent }
export default CalendarDatePicker
